<template>
  <CollapsibleCard
    :class="$style.collapsibleSection"
    :isExpanded="importUsersSectionExpanded"
    title="Import Users"
    @expand="handleToggleSectionExpanded"
  >
    <template v-slot:content>
      <div
        :class="$style.sectionContainer"
      >
        <p
          :class="$style.sectionDescription"
          data-test-id="description"
        >
          {{ sectionDescription }}
        </p>
        <Button
          :isDisabled="!isScimActive"
          text="Start Manual Import"
          type="primary"
          @click="initiateApplicationUserImport"
        />
        <div
          v-if="isCustomApiScheduledImportsEnabled"
          :class="$style.scheduledImportsContainer"
        >
          <ToggleSwitch
            :data-test-id="$testId('scheduledImportsToggle')"
            :label="scheduleToggleOffText"
            :labelOn="scheduleToggleOnText"
            :value="isScheduledImportsEnabled"
            @input="handleScheduledImportsChange"
          />
          <Checkbox
            v-if="isScheduledImportsEnabled"
            :class="$style.scheduledImportsEmailInput"
            :data-test-id="$testId('scheduledImportsEmailInput')"
            label="Receive summary email after each scheduled import"
            :value="true"
          />
        </div>
        <IdmUsersImportOptions
          :countUsers="countUsers"
          :displayName="applicationDisplayName"
          :showOptionsModal="showOptsModal"
          @close="() => toggleShowOptsModal(false)"
          @importUsers="idmImportUsers"
          @selectUsersImport="showUsersSelectionModal"
        />
        <MigratedScimUsersImport
          v-if="showSelectionModal"
          :scimAppId="item.id"
          :showSelectionModal="showSelectionModal"
          @close="() => toogleShowUsersSelectionModal(false)"
          @continue="continueImportProcess"
        />
        <IdmUsersImportResults
          :displayName="applicationDisplayName"
          :resultsJobId="resultsJobId"
          :showResultsModal="showResModal"
          @close="() => toggleShowResModal(false)"
        />
        <HeadingExtraSmall
          v-if="isCustomApiScheduledImportsEnabled"
          text="Import Settings"
          weight="bold"
        >
          Import Settings
        </HeadingExtraSmall>
        <Checkbox
          v-model="allowReactivation"
          :class="$style.sectionCheckbox"
          label="Allow reactivation of users on update"
        />
        <div
          :class="$style.sectionFilter"
        >
          <Checkbox
            v-model="applyFilters"
            :class="$style.sectionCheckbox"
            label="Apply advanced filters on import (optional)"
          />
          <TooltipIcon
            :class="$style.sectionTooltip"
            icon="info"
            orientation="bottom"
            :text="$options.tooltipMessage"
          />
        </div>
        <InputText
          v-if="displayFilter"
          helperText="Enter an optional query supported by your service provider
            to control filtering, sorting, and searching"
          label="Import Filter"
          name="Import Filter"
          rules="isValidQuery"
          showValidationMessage
          :value="importInjectionQuery"
          @input="setImportInjectionQuery"
        />
        <NotificationInPage
          v-if="isCustomApiScheduledImportsEnabled"
          :data-test-id="$testId('provisioningNote')"
          :notification="provisioningNote"
        />
      </div>
    </template>
  </CollapsibleCard>
</template>

<script>
import {
  Button,
  Checkbox,
  CollapsibleCard,
  HeadingExtraSmall,
  InputText,
  NotificationInPage,
  ToggleSwitch,
  TooltipIcon,
  Types,
} from '@jumpcloud/ui-components';
import { mapActions, mapGetters, mapState } from 'vuex';
import IdmUsersImportOptions from 'client/applications/import/IdmUsersImportOptions';
import IdmUsersImportResults from 'client/applications/import/IdmUsersImportResults';
import JobApi from 'client/api/v2/JobApi';
import MigratedScimUsersImport from 'client/applications/import/MigratedScimUsersImport';

const ApplicationModel = 'ApplicationModel';
const ScimUsersImportCollection = 'ScimUsersImportCollection';

const provisioningNoteMsg = 'Automatic deprovisioning of users in JumpCloud will occur as part of updates to users if  inactive statuses are defined in the configuration settings';
const tooltipMessage = 'Allows you to enter query parameters supported by this application for returning a subset of users based on certain attributes and in a specific order. Inactive users are never imported.';

export default {
  name: 'IdmUsersImportFilterSection',

  inject: ['$validator'],

  components: {
    Button,
    Checkbox,
    CollapsibleCard,
    HeadingExtraSmall,
    IdmUsersImportOptions,
    IdmUsersImportResults,
    InputText,
    MigratedScimUsersImport,
    NotificationInPage,
    ToggleSwitch,
    TooltipIcon,
  },

  data() {
    return {
      allowReactivation: true,
      applyFilters: false,
      countUsers: 0,
      importUsersSectionExpanded: true,
      isScheduledImportsEnabled: true,
      provisioningNote: new Types.NotificationConfig({
        icon: 'info',
        message: provisioningNoteMsg,
        title: 'Provisioning Note:',
        type: 'informational',
      }),
      resultsJobId: '',
      scheduleToggleOffText: `Schedule hourly imports of users and user updates from ${this.displayName()} (disabled)`,
      scheduleToggleOnText: `Schedule hourly imports of users and user updates from ${this.displayName()} (enabled)`,
      sectionDescription: `Import users and update existing users from ${this.displayName()}.`,
      showOptsModal: false,
      showResModal: false,
      showSelectionModal: false,
    };
  },

  tooltipMessage,

  computed: {
    ...mapState(ScimUsersImportCollection, {
      importInjectionQuery: state => state.features.importInjectionQuery,
    }),

    ...mapState(ApplicationModel, [
      'item',
      'isScimActive',
    ]),

    applicationDisplayName() {
      const { name, displayLabel, displayName } = this.item;
      return (name === 'bookmark' || name === 'saml2') ? displayLabel : displayName;
    },

    displayFilter() {
      return this.applyFilters || !!this.importInjectionQuery;
    },

    isCustomApiScheduledImportsEnabled() {
      return this.$hasFeatureFlag(this.$featureFlags.customApiScheduledImports);
    },
  },

  watch: {
    isScimActive(newVal) {
      this.importUsersSectionExpanded = newVal;
    },
  },

  created() {
    this.setScimApplicationId({ scimAppId: this.item.id });
  },

  beforeMount() {
    if (this.importInjectionQuery) {
      this.applyFilters = true;
    }
    if (!this.isScimActive) {
      this.importUsersSectionExpanded = false;
    }
  },

  methods: {
    ...mapActions(ScimUsersImportCollection, [
      'fetchCollection',
      'setImportInjectionQuery',
      'setPage',
      'setScimApplicationId',
    ]),

    ...mapGetters(ApplicationModel, [
      'displayName',
    ]),

    ...mapGetters(ScimUsersImportCollection, [
      'getTotalCountOfAllItems',
      'getImportInjectionQuery',
    ]),

    continueImportProcess(jobId) {
      this.resultsJobId = jobId;
      this.toogleShowUsersSelectionModal(false);
      this.toggleShowResModal(true);
    },

    async getUsersInfo() {
      this.setPage({ pageNumber: 0, limit: 10 });
      await this.fetchCollection();
      this.countUsers = this.getTotalCountOfAllItems();
    },

    handleScheduledImportsChange(value) {
      this.isScheduledImportsEnabled = value;
    },

    handleToggleSectionExpanded() {
      this.importUsersSectionExpanded = !this.importUsersSectionExpanded;
    },

    async idmImportUsers(importCase) {
      this.resultsJobId = await JobApi.createImportUsersJob(
        this.item.id,
        this.getImportInjectionQuery(),
        importCase,
      );

      this.toggleShowOptsModal(false);
      this.toggleShowResModal(true);
    },

    initiateApplicationUserImport() {
      this.getUsersInfo();
      this.toggleShowOptsModal(true);
    },

    showUsersSelectionModal() {
      this.toggleShowOptsModal(false);
      this.toogleShowUsersSelectionModal(true);
    },

    toggleShowOptsModal(value) {
      this.showOptsModal = value;
    },

    toggleShowResModal(value) {
      this.showResModal = value;
    },

    toogleShowUsersSelectionModal(value) {
      this.showSelectionModal = value;
    },
  },
};
</script>

<style module>
@value (lightShadow3) from '~css/shadows.css';

.collapsibleSection {
  box-shadow: lightShadow3;
  margin-bottom: 0.5rem;
  min-width: 350px;
}

.collapsibleSection > div {
  min-width: 350px;
  padding: 0 14px;
}

.scheduledImportsContainer {
  border-bottom: 0.0625rem solid var(--jcGrey200);
  display: flex;
  flex-direction: column;
  margin-bottom: 1.25rem;
  padding: 1.25rem 0;
}

.scheduledImportsEmailInput {
  margin-top: 0.9375rem;
  padding-left: 3.6rem;
}

.sectionContainer {
  padding: 0 0.3rem;
}

.sectionContainer > button > span {
  font-size: 14px;
}

.sectionDescription {
  margin-bottom: var(--jc-spacer-medium);
}

.sectionFilter {
  display: flex;
  margin: 14px 0;
}

.sectionCheckbox {
  margin: 14px 0;
}

.sectionTooltip {
  margin-top: 5px;
}

</style>
